import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as global from "../data/global";
import { MuiMaterials } from "../@index.js";
import { createApiEndpoint } from "../index.js";
import {
    WindowChrome,
    WindowChromeStatusBanner,
    ModalBody,
    ModalContentTile,
    CenteredLoadingOverlay,
} from "./windowChrome/index.js";

function formatDateDisplay(value) {
    if (!value) return "—";
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const ManufacturedDeviceView = () => {
    const { manufacturedDeviceGuid } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [device, setDevice] = useState(null);
    const [error, setError] = useState("");

    const loadDevice = useCallback(async () => {
        if (!manufacturedDeviceGuid || !global.gw.email) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError("");
        try {
            const res = await createApiEndpoint().fetchManufacturedDevice(
                "direct",
                manufacturedDeviceGuid
            );
            const detail = res?.data?.fetchManufacturedDevice;
            if (!detail) {
                setError("Device not found.");
                setDevice(null);
            } else {
                setDevice(detail);
            }
        } catch (e) {
            console.warn("ManufacturedDeviceView: load failed", e);
            setError("Failed to load device.");
        } finally {
            setLoading(false);
        }
    }, [manufacturedDeviceGuid]);

    useEffect(() => {
        loadDevice();
    }, [loadDevice]);

    return (
        <WindowChrome
            title="Device record"
            onClose={() => navigate("/sites")}
            loadingOverlay={loading ? <CenteredLoadingOverlay /> : null}
            statusBanner={
                error ? (
                    <WindowChromeStatusBanner status={error} onDismiss={() => setError("")} />
                ) : null
            }
        >
            <ModalBody>
                <ModalContentTile sx={{ overflowY: "auto" }}>
                    {!loading && !error && device ? (
                        <MuiMaterials.Box sx={{ width: "100%", maxWidth: 560, mx: "auto" }}>
                            <MuiMaterials.Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                {device.name}
                            </MuiMaterials.Typography>
                            {device.description ? (
                                <MuiMaterials.Typography variant="body1" sx={{ mb: 2 }}>
                                    {device.description}
                                </MuiMaterials.Typography>
                            ) : null}
                            <MuiMaterials.Typography variant="body1" sx={{ mb: 1 }}>
                                <strong>Manufacture date:</strong> {formatDateDisplay(device.manufactureDate)}
                            </MuiMaterials.Typography>
                            <MuiMaterials.Typography variant="body1" sx={{ mb: 1 }}>
                                <strong>Built by:</strong> {device.builtBy}
                            </MuiMaterials.Typography>
                            <MuiMaterials.Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                                Recorded {formatDateDisplay(device.dateCreated)}
                            </MuiMaterials.Typography>
                            <MuiMaterials.Typography variant="subtitle2" sx={{ mb: 1 }}>Parts used</MuiMaterials.Typography>
                            {device.components?.length ? (
                                <MuiMaterials.Table size="small">
                                    <MuiMaterials.TableHead>
                                        <MuiMaterials.TableRow>
                                            <MuiMaterials.TableCell>Component</MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell>Part #</MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell align="right">Qty</MuiMaterials.TableCell>
                                        </MuiMaterials.TableRow>
                                    </MuiMaterials.TableHead>
                                    <MuiMaterials.TableBody>
                                        {[...device.components]
                                            .sort((a, b) =>
                                                (a.componentName || "").localeCompare(
                                                    b.componentName || "",
                                                    undefined,
                                                    { sensitivity: "base" }
                                                )
                                            )
                                            .map((c) => (
                                            <MuiMaterials.TableRow key={`${c.manufacturingComponentId}-${c.componentName}`}>
                                                <MuiMaterials.TableCell>{c.componentName}</MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell>{c.partNumber || "—"}</MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell align="right">{c.quantity}</MuiMaterials.TableCell>
                                            </MuiMaterials.TableRow>
                                        ))}
                                    </MuiMaterials.TableBody>
                                </MuiMaterials.Table>
                            ) : (
                                <MuiMaterials.Typography variant="body2">No components listed.</MuiMaterials.Typography>
                            )}
                        </MuiMaterials.Box>
                    ) : null}
                </ModalContentTile>
            </ModalBody>
        </WindowChrome>
    );
};

export default ManufacturedDeviceView;
